/**
 * calendarStream — the one place four tables become one list of
 * things in a day.
 *
 * CAL-PRO-4. A photographer's day is spread over four tables:
 * `calendar_events` (their own blocks, and anything synced in from
 * Google, Microsoft or Apple), `bookings` (slots clients picked
 * themselves through a /book/:slug link), and projects, which carry
 * both a due date and milestones with dates of their own. Flattening
 * those into one stream, in one shape, is what every surface that
 * shows "what's on" needs first.
 *
 * It lived inside `NewCalendar` as a hundred-line memo, which was
 * fine while the calendar was the only thing that wanted it. It isn't:
 * Rennan asked for *"important things on your today page and dashboard
 * page"*, and the dashboard currently counts "shoots today" off
 * project dates alone — it has never read `calendar_events` at all. A
 * second flattening written for the dashboard would disagree with the
 * calendar inside a month, and the disagreement would be invisible
 * until a studio noticed their dashboard was missing a shoot.
 *
 * So: one hook, two shapes out of it.
 *
 *   `rows`   the internal shape, which keeps `rawId` — the
 *            `calendar_events` row behind an event, when there is
 *            one. Delete and drag both need it, and both are allowed
 *            only where it exists.
 *   `events` the shape the UI renders, `DetailedCalendarEvent`,
 *            mapped once for everything rather than per view.
 *   `byId`   the same rows, for O(1) lookups from a click handler.
 *
 * The ids are prefixed per source (`evt-`, `booking-`, `-due`)
 * because a project's id and a calendar row's id can collide and
 * `byId` would silently lose one.
 */
import { useMemo } from 'react';
import { useProjects } from '@/services/projects';
import { useCalendarEvents } from '@/services/calendarEvents';
import { useBookings, useBookingLinks } from '@/services/scheduler';
import { asDay } from '@/lib/dates';
import type { DetailedCalendarEvent } from '@/components/calendar/EventDetailsModal';

export type CalendarEventType = 'due_date' | 'milestone' | 'personal' | 'booking';

/**
 * What this file reads of each table, and nothing more.
 *
 * Written out rather than left as `any`, which is how they arrived
 * when this was lifted out of NewCalendar. The point is less the type
 * checking than the record: these say which columns the whole
 * calendar depends on, and the sync-id fields below are the ones
 * CAL-PRO-3b found were not all present in the live database.
 */
interface StreamProject {
  id: string;
  name?: string | null;
  due_date?: string | null;
  client?: { name?: string | null } | null;
  milestones?: { id: string; name?: string | null; due_date?: string | null }[] | null;
}

interface StreamEvent {
  id: string;
  title: string;
  start_at: string;
  end_at?: string | null;
  all_day?: boolean | null;
  location?: string | null;
  description?: string | null;
  /** Older rows wrote the body here. */
  notes?: string | null;
  image_url?: string | null;
  meeting_url?: string | null;
  project_id?: string | null;
  /** Optional, every one of them: see the note at `source` below. */
  synced_from_apple?: boolean | null;
  synced_from_microsoft?: boolean | null;
  synced_from_google?: boolean | null;
  apple_external_id?: string | null;
  ms_event_id?: string | null;
  google_event_id?: string | null;
}

interface StreamBooking {
  id: string;
  status?: string | null;
  booking_link_id?: string | null;
  start_at: string;
  end_at?: string | null;
  client_name: string;
  notes?: string | null;
}

interface StreamLink {
  id: string;
  name: string;
  duration_minutes?: number | null;
  location_details?: string | null;
}

export interface CalendarStreamRow {
  id: string;
  /** Underlying `calendar_events` row id — used for delete + edit.
   *  Absent for anything the calendar does not own: a booking, a
   *  project due date, a milestone. */
  rawId?: string;
  title: string;
  subtitle: string;
  date: Date;
  /** End time. Defaults to start + 60min for events without one. */
  endAt: Date;
  allDay?: boolean;
  type: CalendarEventType;
  meetingUrl?: string;
  location?: string;
  description?: string;
  imageUrl?: string;
  projectId?: string;
  /** Origin of the event — controls the source badge. */
  source: DetailedCalendarEvent['source'];
}

/**
 * The flattening itself, with no React in it.
 *
 * PLANNER-4b — pulled out of the hook so the MARKETING page can run
 * the same code over the Preview Studio cast. `CalendarReal` used to
 * be a hand-kept copy of the calendar screen, which is how this repo
 * came to have four of them; a marketing device that renders the real
 * component over injected rows cannot drift from it.
 */
export function buildCalendarStream(input: {
  projects?: StreamProject[] | null;
  events?: StreamEvent[] | null;
  bookings?: StreamBooking[] | null;
  bookingLinks?: StreamLink[] | null;
}): CalendarStreamRow[] {
  const projects = input.projects ?? [];
  const standaloneEvents = input.events ?? [];
  const bookings = input.bookings ?? [];
  const bookingLinks = input.bookingLinks ?? [];

  const out: CalendarStreamRow[] = [];

  projects?.forEach((p) => {
    const clientName = p.client?.name || 'No client';
    if (p.due_date) {
      const date = asDay(p.due_date);
      out.push({
        id: `${p.id}-due`,
        title: p.name ?? 'Project due',
        subtitle: clientName,
        date,
        endAt: date,
        allDay: true,
        type: 'due_date',
        projectId: p.id,
        source: 'orbit',
      });
    }
    p.milestones?.forEach((ms) => {
      if (ms.due_date) {
        const date = asDay(ms.due_date);
        out.push({
          id: ms.id,
          title: ms.name ?? 'Milestone',
          subtitle: clientName,
          date,
          endAt: date,
          allDay: true,
          type: 'milestone',
          projectId: p.id,
          source: 'orbit',
        });
      }
    });
  });

  standaloneEvents.forEach((evt) => {
    const startAt = new Date(evt.start_at);
    const endAt = evt.end_at ? new Date(evt.end_at) : new Date(startAt.getTime() + 60 * 60_000);
    // Where the event came from. Precedence: apple > microsoft >
    // google > orbit; the first thing that lights up wins.
    //
    // CAL-PRO-3 — the flag OR the external id, and the "or" is not
    // belt and braces. `synced_from_google` is added by
    // 20260510_google_calendar_sync.sql, and the types regenerated
    // on 29 Oct against the live database do not have it — nor
    // `google_calendar_id` or `google_etag`, which that migration
    // adds in the same `alter table`. That statement evidently never
    // ran, so `evt.synced_from_google` was permanently `undefined`
    // and every event pulled in from Google was labelled as the
    // studio's own. Harmless while it only picked a badge; not
    // harmless now that CAL-PRO-2 decides from `source` whether an
    // event may be DRAGGED — a studio would have moved a Google
    // event, watched it move, and had the next sync put it back.
    //
    // `google_event_id` does exist and is what the sync function
    // actually writes a Google id into, so it is the dependable
    // signal. Testing both means this is right whether or not
    // Lovable later applies that migration.
    const source: DetailedCalendarEvent['source'] =
      evt.synced_from_apple || evt.apple_external_id ? 'apple'
      : evt.synced_from_microsoft || evt.ms_event_id ? 'microsoft'
      : evt.synced_from_google || evt.google_event_id ? 'google'
      : 'orbit';
    out.push({
      id: `evt-${evt.id}`,
      rawId: evt.id,
      title: evt.title,
      subtitle: evt.project_id
        ? (projects?.find((p) => p.id === evt.project_id)?.client?.name ?? 'Personal')
        : 'Personal',
      date: startAt,
      endAt,
      allDay: !!evt.all_day,
      type: 'personal',
      meetingUrl: evt.meeting_url ?? undefined,
      location: evt.location ?? undefined,
      description: evt.description ?? evt.notes ?? undefined,
      imageUrl: evt.image_url ?? undefined,
      projectId: evt.project_id ?? undefined,
      source,
    });
  });

  bookings.forEach((b) => {
    if (b.status === 'cancelled') return;
    const link = bookingLinks.find((l) => l.id === b.booking_link_id);
    const startAt = new Date(b.start_at);
    const endAt = b.end_at ? new Date(b.end_at) : new Date(startAt.getTime() + (link?.duration_minutes ?? 30) * 60_000);
    out.push({
      id: `booking-${b.id}`,
      title: link ? `${link.name} · ${b.client_name}` : b.client_name,
      subtitle: b.client_name,
      date: startAt,
      endAt,
      type: 'booking',
      location: link?.location_details ?? undefined,
      description: b.notes ?? undefined,
      source: 'booking',
    });
  });

  return out;
}

/** The shape the UI renders. Mapped once, for everything. */
export function toDetailedEvents(rows: CalendarStreamRow[]): DetailedCalendarEvent[] {
  return rows
    .map((e) => ({
      id: e.id,
      title: e.title,
      subtitle: e.subtitle,
      startAt: e.date,
      endAt: e.endAt,
      allDay: e.allDay,
      location: e.location,
      description: e.description,
      meetingUrl: e.meetingUrl,
      imageUrl: e.imageUrl,
      projectId: e.projectId,
      source: e.source,
      type: e.type,
    }));
}

export function useCalendarStream() {
  const { data: rawProjects = [] } = useProjects();
  const { data: rawEvents = [] } = useCalendarEvents();
  const { data: rawBookings = [] } = useBookings();
  const { data: rawLinks = [] } = useBookingLinks();

  // One cast each, at the edge, instead of an `any` per callback. The
  // services return their own generated row types; these narrow them
  // to what the builder reads.
  const events = useMemo(
    () =>
      buildCalendarStream({
        projects: rawProjects as unknown as StreamProject[],
        events: rawEvents as unknown as StreamEvent[],
        bookings: rawBookings as unknown as StreamBooking[],
        bookingLinks: rawLinks as unknown as StreamLink[],
      }),
    [rawProjects, rawEvents, rawBookings, rawLinks],
  );

  // A lookup so click handlers can grab full details in O(1).
  const byId = useMemo(() => {
    const m = new Map<string, CalendarStreamRow>();
    events.forEach((e) => m.set(e.id, e));
    return m;
  }, [events]);

  const detailedEvents = useMemo(() => toDetailedEvents(events), [events]);

  return { rows: events, byId, events: detailedEvents };
}

export default useCalendarStream;
